import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../config/api";
import { BASE_API } from "../../../constants/apiUrls";
import { toast } from "../../../lib/toast";
import {
  CommonOthers,
  ErrorMessage,
  GetCommonData,
  GetSingleDataParams,
  Params,
  PostRequest,
} from "../../../types";
import {
  CreateCommunityForumData,
  GetCommunityForumData,
  GetCommunityForumDataParams,
  GetSingleCommunityForumData,
} from "../../../types/communityForum/communityForum";
import { handleApiError } from "../../../utils/errorHandler";

// create
export const createCommunityForumAsync = createAsyncThunk<
  null,
  PostRequest<CreateCommunityForumData, CommonOthers>
>(
  "admin/createCommunityForum",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(BASE_API.createCommunityForum, data, {
        headers: {},
      });

      reRender(true);
      toast("success", "CommunityForum create successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "CommunityForum create fail");
      return rejectWithValue(error);
    }
  }
);

// get all
export const getAllCommunityForumAsync = createAsyncThunk<
  GetCommonData<GetCommunityForumData>,
  Params<GetCommunityForumDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin/getAllCommunityForum", async ({ params }, { rejectWithValue }) => {
  const { ...rest } = params as GetCommunityForumDataParams;

  try {
    const data = await AXIOS.get(BASE_API.getAllCommunityForum, {
      params: { ...rest },
      headers: {},
    });

    return {
      data: data.data?.data,
      length: data.data?.totalCount,
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Get CommunityForum fail");
    return rejectWithValue(error);
  }
});

// update
export const updateCommunityForumAsync = createAsyncThunk<
  null,
  PostRequest<CreateCommunityForumData, GetSingleDataParams>
>(
  "admin/updateCommunityForum",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updateCommunityForum}/${id}`, data, {
        headers: {},
      });

      reRender && reRender(true);
      toast("success", "CommunityForum update successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "CommunityForum update fail");
      return rejectWithValue(error);
    }
  }
);

// single
export const singleCommunityForumAsync = createAsyncThunk<
  GetSingleCommunityForumData,
  Params<GetSingleDataParams>
>("admin/singleCommunityForum", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(
      `${BASE_API.getSingleCommunityForum}/${params?.id}`,
      {
        headers: {},
      }
    );

    return data.data?.data as GetSingleCommunityForumData;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Single CommunityForum data get fail");
    return rejectWithValue(error);
  }
});


export const toggleLikeAsync = createAsyncThunk(
  "communityForum/toggleLike",
  async ({
      communityForumId,
      commentId,
      replyId,
  }: {
      communityForumId: string;
      commentId: string;
      replyId?: string;
  }) => {
      const { data } = await AXIOS.post(`${BASE_API.toggleLikeForum}`, {
          communityForumId,
          commentId,
          ...(replyId && { replyId }),
      });
      return data;
  }
);